import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import infrastructureWorkImage from "@/assets/infrastructure-work.jpg";
import fiberOpticInstallationImage from "@/assets/fiber-optic-installation.jpg";
import trenchingInfrastructureImage from "@/assets/trenching-infrastructure.jpg";
import streetInfrastructureNewImage from "@/assets/street-infrastructure-new.jpg";
import canalizacionesImage from "@/assets/canalizaciones.jpg";

const ServicesSection = () => {
  const { t } = useTranslation();
  
  // Force refresh
  const services = [
    {
      id: "canalizacion",
      title: t('services.infrastructure.title'),
      description: t('services.infrastructure.description'),
      image: trenchingInfrastructureImage,
      features: t('services.features1')
    },
    {
      id: "instalacion",
      title: t('services.installation.title'),
      description: t('services.installation.description'),
      image: infrastructureWorkImage,
      features: t('services.features2')
    },
    {
      id: "servicios-tecnicos",
      title: t('services.maintenance.title'),
      description: t('services.maintenance.description'),
      image: fiberOpticInstallationImage,
      features: t('services.features3')
    },
    {
      id: "reposicion",
      title: t('services.permits.title'),
      description: t('services.permits.description'),
      image: streetInfrastructureNewImage,
      features: t('services.features4')
    }
  ];

  return (
    <section id="servicios" className="py-20 bg-enausa-light">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-enausa-navy mb-6">
            {t('services.title')}
          </h2>
          <p className="text-lg sm:text-xl text-enausa-gray max-w-3xl mx-auto leading-relaxed px-4">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Main Services */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6 sm:gap-8 max-w-7xl mx-auto px-4">
          {services.map((service) => (
            <Card key={service.id} className="overflow-hidden shadow-medium hover:shadow-strong transition-all duration-300 group flex flex-col h-full">
              <div className="relative overflow-hidden">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                  decoding="async"
                  width="346"
                  height="256"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 346px"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-enausa-navy/80 to-transparent"></div>
              </div>
              <CardHeader>
                <CardTitle className="text-xl text-enausa-navy">{service.title}</CardTitle>
                <CardDescription className="text-enausa-gray">{service.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col justify-between">
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, index) => (
                     <li key={index} className="flex items-start text-sm text-enausa-gray">
                       <CheckCircle className="w-4 h-4 text-enausa-navy mt-0.5 mr-3 flex-shrink-0" />
                       {feature}
                     </li>
                  ))}
                </ul>
                <Button 
                  variant="enausa-outline" 
                  size="sm" 
                  className="w-full"
                  onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  {t('nav.contact')}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-hero rounded-2xl p-6 sm:p-8 md:p-12 text-enausa-text-on-navy shadow-strong mx-4">
            <h3 className="text-2xl sm:text-3xl font-bold mb-4">
              {t('contact.title')}
            </h3>
            <p className="text-lg sm:text-xl mb-6 sm:mb-8 text-enausa-text-on-navy/80">
              {t('contact.subtitle')}
            </p>
            <Button 
              variant="enausa-outline" 
              size="xl" 
              className="w-full sm:w-auto border-enausa-text-on-navy text-enausa-text-on-navy hover:bg-enausa-text-on-navy hover:text-enausa-navy"
              onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('hero.cta')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;